import { Col } from "react-bootstrap";
import {
  Control,
  Controller,
  DeepMap,
  FieldError,
  FieldValues,
  Path,
  UseFormRegisterReturn,
} from "react-hook-form";
import Select, {
  MultiValue,
  Options,
  SingleValue,
  StylesConfig,
} from "react-select";
import MaterialIcon, { MtIcon } from "../material-icon/material-icon";
import styles from "./app-select.module.scss";

export type FieldErrors<TFieldValues extends FieldValues = FieldValues> =
  DeepMap<TFieldValues, FieldError>;

export interface Option {
  value: string;
  label: string | JSX.Element;
  data?: any;
}

interface IProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
  React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
  >,
  "name"
> {
  name?: Path<TFieldValues>;
  isWithOutBorder?: boolean;
  icon?: MtIcon;
  errorMessage?: string;
  label?: string;
  onIconClick?: () => void;
  onSelectOption?: (option: Option) => void;
  options: Option[];
  register?: UseFormRegisterReturn;
  errors?: FieldErrors;
  control?: Control<TFieldValues>;
  isMultiSelect?: boolean;
  onChangeOption?: (option: Option) => void;
  selectedValue?: Option | Option[] | undefined;
  borderRadiusStyle?: "Right" | "Left";
  menuHeight?: string;
  isTimeSelection?: boolean;
  fontSize?: string;
  labelClassName?: string;
  fontWeight?: string;
  fontColor?: string;
  placeholder?: string;
  closeMenuOnSelect?: boolean;
  isRequired?: boolean;
  isOptionsDisabled?:
    | ((option: any, selectValue: Options<any>) => boolean)
    | undefined;
}

const AppSelect = <TFieldValues extends FieldValues = FieldValues>(
  props: IProps<TFieldValues>,
) => {
  const {
    isWithOutBorder,
    icon,
    className,
    fontSize,
    labelClassName = "font-size-16",
    fontWeight,
    fontColor,
    label,
    options,
    isRequired = false,
    errors,
    isMultiSelect,
    control,
    onChangeOption,
    selectedValue,
    borderRadiusStyle,
    menuHeight = "300px",
    disabled,
    isTimeSelection,
    closeMenuOnSelect,
    id,
    placeholder,
    isOptionsDisabled,
    ...rest
  } = props;

  const getBorderRadiusStyle = () => {
    switch (borderRadiusStyle) {
      case "Right":
        return "12px 0 0 12px";
      case "Left":
        return "0px 12px 12px 0px";
      default:
        return "12px";
    }
  };

  const style: StylesConfig<Option, boolean> = {
    control: (base) => ({
      ...base,
      border: "0 !important",
      boxShadow: "0 !important",
      "&:hover": {
        border: "0 !important",
      },
      borderRadius: "12px",
      height: "44px",
      cursor: "pointer",
      backgroundColor: "transparent",
      opacity: disabled ? "0.5" : "",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: fontSize || "14px",
      fontWeight: fontWeight,
      color: fontColor || "var(--light)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--light)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--light)",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--light)",
      "&:hover": {
        color: "var(--dark)",
      },
    }),
    option: (defaultStyles, state) => ({
      ...defaultStyles,
      color: state.isSelected ? "var(--primary)" : "var(--dark)",
      backgroundColor: state.isSelected
        ? "var(--line)"
        : state.isFocused
          ? "var(--line)"
          : "transparent",
      "&:hover": {
        backgroundColor: "var(--line)",
        color: "var(--dark)",
      },
      "&:active": {
        backgroundColor: "var(--line)",
      },
      fontSize: fontSize || "14px",
      borderBottom: isTimeSelection ? "0.5px solid var(--line)" : undefined,
    }),
    menu: (defaultStyles) => ({
      ...defaultStyles,
      borderRadius: "10px",
      boxShadow: "0px 6px 16px 0px var(--card-shadow)",
      zIndex: 3,
      backgroundColor: "var(--input-bg)",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: menuHeight,
      overflow: "auto",
      borderRadius: "10px",
      fontSize: fontSize || "14px",
      backgroundColor: "var(--input-bg)",
    }),
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: "var(--dark)",
      backgroundColor: (data as { color?: string })?.color || "var(--line)",
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: "#D84560",
      backgroundColor: (data as { color?: string })?.color || "var(--line)",
      ":hover": {
        color: "#D84560",
      },
    }),
  };

  return (
    <div
      className={`app-input-wrapper ${className} ${
        disabled ? `cursor-disabled` : ``
      }`}
    >
      {label && (
        <>
          <label
            className={`${disabled ? styles.opacity : ``} ${labelClassName}`}
          >
            {label}
          </label>
          <label className="error">{isRequired && "*"}</label>
        </>
      )}
      <div
        className={`${isWithOutBorder ? "" : styles.appSelectInput} ${
          rest.name && errors && errors[rest.name]?.message && styles.hasDanger
        } d-flex`}
        style={{ borderRadius: `${getBorderRadiusStyle()}` }}
      >
        {icon && (
          <Col className="col-auto">
            <MaterialIcon icon={icon} className="mt-2" />
          </Col>
        )}
        {control ? (
          <Col>
            <Controller
              name={rest.name as Path<TFieldValues>}
              control={control}
              render={({ field }) => (
                <Select<Option, boolean>
                  {...field}
                  options={options}
                  styles={style}
                  isMulti={isMultiSelect}
                  placeholder={
                    <div className={styles[`placeholder-text`]}>
                      {placeholder}
                    </div>
                  }
                  components={
                    isTimeSelection
                      ? {
                          IndicatorSeparator: () => null,
                          DropdownIndicator: () => null,
                        }
                      : { IndicatorSeparator: () => null }
                  }
                  isDisabled={disabled}
                  closeMenuOnSelect={closeMenuOnSelect}
                  id={id}
                  isOptionDisabled={isOptionsDisabled}
                />
              )}
            />
          </Col>
        ) : (
          <Col>
            <Select<Option, boolean>
              options={options}
              styles={style}
              isMulti={isMultiSelect}
              placeholder={
                <div className={styles[`placeholder-text`]}>{placeholder}</div>
              }
              components={
                isTimeSelection
                  ? {
                      IndicatorSeparator: () => null,
                      DropdownIndicator: () => null,
                    }
                  : { IndicatorSeparator: () => null }
              }
              onChange={(
                newValue: SingleValue<Option> | MultiValue<Option>,
              ) => {
                const option: Option = newValue as Option;
                onChangeOption && onChangeOption(option);
              }}
              value={selectedValue}
              isDisabled={disabled}
              closeMenuOnSelect={closeMenuOnSelect}
              id={id}
              isOptionDisabled={isOptionsDisabled}
            />
          </Col>
        )}
      </div>
      {rest.name && (
        <span className="error">{errors && errors[rest.name]?.message}</span>
      )}
    </div>
  );
};

export default AppSelect;
